// ESLint gets confused by curried selectors, making it impossible to satisfy all doc-related lints
import { curryAppSelector } from "@/store/reselect";
import { RootState } from "@/store/store";
import { EMPTY_ARRAY } from "@faro-lotv/foundation";
import { GUID, isValid } from "@faro-lotv/ielement-types";
import {
  CaptureTreeEntityRevision,
  CaptureTreeEntityType,
  CreateOrUpdateClusterEntityParams,
  CreateOrUpdateRootEntityRequestBody,
  CreateOrUpdateScanEntityParams,
  RegistrationRevision,
} from "@faro-lotv/service-wires";
import { createSelector } from "@reduxjs/toolkit";
import { EntityMap } from "./revision-slice";
import { RevisionTransformCache } from "./revision-transform-cache";
import { isEntityDeletedOnRevision } from "./selector-helpers";

// basic revision selectors

/**
 * @param state The current application state.
 * @returns the information about the registration revision
 */
export function selectRegistrationRevision(
  state: RootState,
): RegistrationRevision | undefined {
  return state.revision.registrationRevision;
}

/**
 * @param state The current application state.
 * @returns A map from entity ID to the world transform of the entity.
 */
export function selectRevisionTransformCache(
  state: RootState,
): RevisionTransformCache {
  return state.revision.transformCache;
}

/**
 * @param state The current application state.
 * @returns A map from entity ID to the entity definition.
 */
export function selectRevisionEntityMap(state: RootState): EntityMap {
  return state.revision.entityMap;
}

// derived revision selectors

/**
 * @param state The current application state.
 * @returns All loaded entities in the revision.
 */
export const selectRevisionEntities = createSelector(
  [selectRevisionEntityMap],
  // Filtering here is only necessary to make TS happy, due to the index types
  (entityMap) => Object.values(entityMap).filter(isValid),
);

/**
 * @param state The current application state.
 * @returns All loaded entities in the revision that are fixed.
 */
export const selectFixedRevisionEntities = createSelector(
  [selectRevisionEntityMap],
  // Filtering here is only necessary to make TS happy, due to the index types
  (entityMap) =>
    Object.values(entityMap).filter(
      (entity) => isValid(entity) && entity.isFixed,
    ),
);

/**
 * @param state The current application state.
 * @returns All loaded entities in the revision that are fixed.
 */
export const selectFixedRevisionEntityIds = createSelector(
  [selectFixedRevisionEntities],
  (entities) => entities.map((entity) => entity?.id),
);

/**
 * @param state The current application state.
 * @returns All loaded entities that have been transformed in the revision.
 */
export const selectTransformedEntities = createSelector(
  [selectRevisionEntities],
  (entities) => entities.filter((entity) => entity.diff?.Pose !== undefined),
);

/**
 * @param state The current application state.
 * @returns All loaded entity ids that have been transformed in the revision.
 */
export const selectTransformedEntityIds = createSelector(
  [selectTransformedEntities],
  (transformedEntities) => transformedEntities.map((entity) => entity.id),
);

/**
 * @param state The current application state.
 * @param id The unique identifier of the entity to retrieve.
 * @returns All loaded entities in the revision that are not marked for removal.
 */
export const selectRevisionEntityById = curryAppSelector(
  createSelector(
    [selectRevisionEntityMap, (state: RootState, id: GUID) => id],
    (entityMap, id) => entityMap[id],
  ),
);

/**
 * @param state The current application state.
 * @returns the root entity
 */
export const selectRootEntity = createSelector(
  [selectRevisionEntities],
  (entities) =>
    entities.find((entity) => entity.type === CaptureTreeEntityType.root),
);

export type UpdatedRevisionEntities = {
  /** The parameters to update the root (if it got changed). */
  updatedRoot?: CreateOrUpdateRootEntityRequestBody;
  /** The parameters to update the clusters. */
  updatedClusters: CreateOrUpdateClusterEntityParams[];
  /** The parameters to update the scans. */
  updatedScans: CreateOrUpdateScanEntityParams[];
};

/**
 * @param _state The current application state.
 * @param id The ID of the revision entity.
 * @returns The revision entity with the given ID or `undefined` if it's not loaded.
 */
export const selectRevisionEntity = curryAppSelector(
  createSelector(
    [selectRevisionEntityMap, (_state, id?: GUID) => id],
    (entityMap, id): CaptureTreeEntityRevision | undefined =>
      id ? entityMap[id] : undefined,
  ),
);

export type ChildrenMap = Record<GUID, CaptureTreeEntityRevision[] | undefined>;

/**
 * @param state the current application state
 * @returns a cached map of entities to their children (excluding deleted entities)
 */
export const selectRevisionEntityChildrenMap = createSelector(
  [(state: RootState) => state.revision.entityMap],
  (entityMap) => {
    const map: ChildrenMap = {};

    for (const entity of Object.values(entityMap)) {
      if (!entity?.parentId) continue;

      // Skip deleted entities - they should not be considered as valid children
      if (isEntityDeletedOnRevision(entity)) continue;

      let childrenOfParent = map[entity.parentId];

      if (!childrenOfParent) {
        childrenOfParent = [];
        map[entity.parentId] = childrenOfParent;
      }

      childrenOfParent.push(entity);
    }

    return map;
  },
);

/**
 * @param _state the current state of the application
 * @param entity the id of the revision entity to get the siblings for
 * @returns an array of names of the siblings for the given entity, excluding the entity itself
 */
export const selectRevisionEntitySiblings = curryAppSelector(
  createSelector(
    [
      selectRevisionEntityChildrenMap,
      (_state, entity: CaptureTreeEntityRevision) => entity,
    ],
    (entityChildrenMap, entity) =>
      entity.parentId
        ? (entityChildrenMap[entity.parentId]?.filter(
            (sibling) => sibling.id !== entity.id,
          ) ?? EMPTY_ARRAY)
        : EMPTY_ARRAY,
  ),
);
